import React from 'react';
import { useMatricula } from '../hooks/useMatricula';
import { FormSelect } from '../components/form/FormFields';
import { UsuarioListItem } from '../types/usuario.types';
import { MatriculaPagoInfo, MatriculaPensionesResumen } from '../types/matricula.types';
import { TipoDescuento, DescuentoEstudiante } from '../types/pago.types';
import FumPage from 'pages/FumPage';

// ============================================================
// MatriculaFormPage
// – Si el estudiante ya tiene matrícula: pinta los selects con
//   los datos del backend y muestra un resumen informativo.
// – Si no tiene: formulario en blanco para crear.
// ============================================================

interface Props {
  estudiante: UsuarioListItem;
  onSuccess?: () => void;
  onCancel?:  () => void;
}

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-slate-400" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const MatriculaFormPage: React.FC<Props> = ({ estudiante, onSuccess = () => {}, onCancel = () => {} }) => {
  const {
    form, errors, submitting, submitError, loading,
    matriculaActual,
    añosEscolares, grados, secciones,
    handleChange, handleSubmit,
    tarifaForm, tarifaSubmitting, tarifaError, tarifaSuccess,
    handleTarifaChange, handleAjustarTarifas,
    deleting, deleteError, handleEliminar,
    tiposDescuento, descuentos, descuentoAddId, setDescuentoAddId,
    descuentoAdding, descuentoError,
    handleAsignarDescuento, handleEliminarDescuento,
  } = useMatricula(estudiante.idUsuario, onSuccess);

  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'matricula' | 'fum'>('matricula');
  const isEditing = matriculaActual !== null;

  return (
    <div className="max-w-xl mx-auto">

      {/* Cabecera */}
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onCancel}
          className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            {loading ? 'Cargando...' : isEditing ? 'Modificar Matrícula' : 'Registrar Matrícula'}
          </h1>
          <p className="text-slate-400 text-sm">
            {isEditing ? `Matrícula #${matriculaActual?.idMatricula}` : 'Matricula al estudiante en el año escolar activo'}
          </p>
        </div>
      </div>

      {/* Card del estudiante */}
      <div className="bg-gradient-to-r from-green-700 to-green-500 rounded-2xl p-5 mb-5 flex items-center gap-4 shadow-md">
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center
                        text-white font-bold text-lg flex-shrink-0">
          {estudiante.nombres.charAt(0)}{estudiante.apellidos.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold">{estudiante.nombres} {estudiante.apellidos}</p>
          <p className="text-green-100 text-sm">DNI: {estudiante.dni} · @{estudiante.username}</p>
        </div>
        {!loading && (
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full flex-shrink-0
            ${isEditing ? 'bg-white/20 text-white' : 'bg-yellow-300 text-yellow-900'}`}>
            {isEditing ? '✓ Matriculado' : 'Sin matrícula'}
          </span>
        )}
      </div>

      {/* Tabs (solo en edición) */}
      {isEditing && (
        <div className="flex gap-1 mb-5 bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab('matricula')}
            className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors
              ${activeTab === 'matricula' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            Matrícula
          </button>
          <button
            onClick={() => setActiveTab('fum')}
            className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors
              ${activeTab === 'fum' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            Apoderado / FUM
          </button>
        </div>
      )}

      {/* Contenido tab FUM */}
      {isEditing && activeTab === 'fum' && matriculaActual && (
        <FumPage
          matriculaId={matriculaActual.idMatricula}
          estudianteNombre={`${estudiante.apellidos}, ${estudiante.nombres}`}
          onBack={() => setActiveTab('matricula')}
        />
      )}

      {/* Contenido tab Matrícula */}
      {(!isEditing || activeTab === 'matricula') && (<>

      {/* Resumen de matrícula actual (solo si ya existe) */}
      {!loading && isEditing && matriculaActual && (
        <>
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-4">
            <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-3">
              Matrícula actual registrada
            </p>
            <div className="grid grid-cols-2 gap-3">
              <InfoCard label="Año Escolar" value={String(matriculaActual.anio)} />
              <InfoCard label="Grado"       value={`${matriculaActual.gradoNombre} – ${matriculaActual.gradoNivel}`} />
              <InfoCard label="Sección"     value={`Sección ${matriculaActual.seccionNombre}`} />
              <InfoCard label="Fecha"       value={matriculaActual.fechaMatricula ?? '—'} />
            </div>
            <p className="text-xs text-green-600 mt-3">
              Puedes modificar los datos en el formulario de abajo.
            </p>
          </div>

          {/* Panel de pagos */}
          {(matriculaActual.pagoMatricula || matriculaActual.pensiones) && (
            <PanelPagos
              pago={matriculaActual.pagoMatricula}
              pensiones={matriculaActual.pensiones}
            />
          )}
        </>
      )}

      {/* Descuentos asignados (solo en edición) */}
      {!loading && isEditing && (
        <PanelDescuentos
          tiposDescuento={tiposDescuento}
          descuentos={descuentos}
          addId={descuentoAddId}
          onAddIdChange={setDescuentoAddId}
          adding={descuentoAdding}
          error={descuentoError}
          onAsignar={handleAsignarDescuento}
          onEliminar={handleEliminarDescuento}
        />
      )}

      {/* Ajuste de tarifas (solo en edición) */}
      {!loading && isEditing && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-5">
          <div className="px-6 py-4 bg-gradient-to-r from-indigo-700 to-indigo-500">
            <h2 className="text-white font-bold">Ajustar Tarifas</h2>
            <p className="text-white/70 text-xs mt-0.5">
              Deja en blanco los campos que no deseas modificar
            </p>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Costo Matrícula (S/)
                </label>
                {matriculaActual?.pagoMatricula && (
                  <p className="text-xs text-slate-400 -mt-0.5">
                    Actual: <span className="font-semibold text-slate-600">
                      {sol(matriculaActual.pagoMatricula.montoTarifa)}
                    </span>
                  </p>
                )}
                <input
                  type="number" min={0} step={0.01}
                  name="costoMatricula"
                  value={tarifaForm.costoMatricula}
                  onChange={handleTarifaChange}
                  placeholder="Sin cambio"
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800
                             outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Costo Pensión mensual (S/)
                </label>
                {matriculaActual?.pensiones && (
                  <p className="text-xs text-slate-400 -mt-0.5">
                    Actual: <span className="font-semibold text-slate-600">
                      {sol(matriculaActual.pensiones.montoMensual)}
                    </span>
                  </p>
                )}
                <input
                  type="number" min={0} step={0.01}
                  name="costoPension"
                  value={tarifaForm.costoPension}
                  onChange={handleTarifaChange}
                  placeholder="Sin cambio"
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800
                             outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                />
              </div>
            </div>

            {tarifaError && (
              <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{tarifaError}</p>
            )}
            {tarifaSuccess && (
              <p className="text-sm text-green-700 bg-green-50 rounded-xl px-4 py-2">
                Tarifas actualizadas correctamente.
              </p>
            )}

            <div className="flex justify-end">
              <button onClick={handleAjustarTarifas} disabled={tarifaSubmitting}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                           text-white text-sm font-semibold transition-colors disabled:opacity-60 shadow-sm">
                {tarifaSubmitting ? <SpinnerIcon /> : (
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                  </svg>
                )}
                Aplicar ajuste
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulario */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className={`px-6 py-4 ${isEditing
          ? 'bg-gradient-to-r from-amber-700 to-amber-500'
          : 'bg-gradient-to-r from-green-800 to-green-600'}`}>
          <h2 className="text-white font-bold">
            {isEditing ? 'Modificar datos de matrícula' : 'Datos de Matrícula'}
          </h2>
          <p className="text-white/70 text-xs mt-0.5">
            Año escolar, grado y sección
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
            <SpinnerIcon />
            <span className="text-sm">Consultando datos del backend...</span>
          </div>
        ) : (
          <div className="p-6 space-y-4">

            <FormSelect
              label="Año Escolar"
              name="anioEscolarId"
              value={form.anioEscolarId}
              onChange={handleChange}
              error={errors.anioEscolarId}
              required
              placeholder="Seleccionar año escolar"
              options={añosEscolares.map(a => ({
                value: a.idAnioEscolar,
                label: `${a.anio}${a.idAnioEscolar === Number(form.anioEscolarId) && isEditing ? ' (actual)' : ''}`,
              }))}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormSelect
                label="Grado"
                name="gradoId"
                value={form.gradoId}
                onChange={handleChange}
                error={errors.gradoId}
                required
                placeholder="Seleccionar grado"
                options={grados.map(g => ({
                  value: g.idGrado,
                  label: `${g.nombre} – ${g.nivel}`,
                }))}
              />
              <FormSelect
                label="Sección"
                name="seccionId"
                value={form.seccionId}
                onChange={handleChange}
                error={errors.seccionId}
                required
                placeholder="Seleccionar sección"
                options={secciones.map(s => ({
                  value: s.idSeccion,
                  label: `Sección ${s.nombre}`,
                }))}
              />
            </div>

            {submitError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                </svg>
                <span className="text-red-600 text-sm">{submitError}</span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 space-y-3">
          {/* Error de eliminación */}
          {deleteError && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{deleteError}</p>
          )}

          {/* Confirmación de eliminación */}
          {confirmDelete && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-red-500 flex-shrink-0">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
              </svg>
              <p className="text-sm text-red-700 flex-1">
                ¿Eliminar matrícula #{matriculaActual?.idMatricula}? Esta acción no se puede deshacer.
              </p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmDelete(false)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600
                             hover:bg-slate-200 transition-colors">
                  Cancelar
                </button>
                <button onClick={handleEliminar} disabled={deleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold
                             bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60">
                  {deleting ? <SpinnerIcon /> : null}
                  Sí, eliminar
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <div className="flex gap-2">
              <button onClick={onCancel}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
                Cancelar
              </button>
              {isEditing && !confirmDelete && (
                <button onClick={() => setConfirmDelete(true)} disabled={deleting}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold
                             text-red-600 hover:bg-red-50 border border-red-200 transition-colors
                             disabled:opacity-60">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z" clipRule="evenodd"/>
                  </svg>
                  Eliminar
                </button>
              )}
            </div>
            <button onClick={handleSubmit} disabled={submitting || loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-bold text-white
                          shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed
                          ${isEditing
                            ? 'bg-amber-600 hover:bg-amber-700'
                            : 'bg-green-600 hover:bg-green-700'}`}>
              {submitting ? <SpinnerIcon /> : (
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
                </svg>
              )}
              {isEditing ? 'Actualizar Matrícula' : 'Guardar Matrícula'}
            </button>
          </div>
        </div>
      </div>

      </>)}
    </div>
  );
};

// ── Sub-componente tarjeta de dato ────────────────────────────
const InfoCard = ({ label, value }: { label: string; value: string }) => (
  <div className="bg-white rounded-xl px-4 py-3 border border-green-100">
    <p className="text-xs text-green-600 font-semibold mb-0.5">{label}</p>
    <p className="text-slate-800 text-sm font-bold">{value}</p>
  </div>
);

// ── Sub-componente panel de pagos ─────────────────────────────
const sol = (n: number | null) =>
  n == null ? '—' :
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

const ESTADO_STYLE: Record<string, string> = {
  PENDIENTE: 'bg-amber-100 text-amber-700',
  PAGADO:    'bg-green-100 text-green-700',
  ANULADO:   'bg-slate-100 text-slate-500',
};

const StatBox = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="bg-white rounded-xl px-3 py-2.5 border border-slate-100 text-center">
    <p className="text-xs text-slate-400 leading-tight">{label}</p>
    <p className="font-bold text-slate-800 text-sm mt-0.5 tabular-nums">{value}</p>
    {sub && <p className="text-xs text-slate-400 tabular-nums">{sub}</p>}
  </div>
);

const PanelPagos: React.FC<{
  pago:      MatriculaPagoInfo | null;
  pensiones: MatriculaPensionesResumen | null;
}> = ({ pago, pensiones }) => (
  <div className="space-y-3 mb-5">

    {/* Pago de matrícula */}
    {pago && (
      <div className="rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">
            Pago de Matrícula
          </p>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${ESTADO_STYLE[pago.estado] ?? 'bg-slate-100 text-slate-600'}`}>
            {pago.estado}
          </span>
        </div>
        <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatBox label="Tarifa"    value={sol(pago.montoTarifa)} />
          <StatBox label="Descuento" value={sol(pago.montoDescuento)} />
          <StatBox label="Total"     value={sol(pago.montoFinal)} />
          <StatBox
            label="Pagado"
            value={sol(pago.montoPagado)}
            sub={pago.fechaPago ?? undefined}
          />
        </div>
      </div>
    )}

    {/* Resumen pensiones */}
    {pensiones && (
      <div className="rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">
            Pensiones
          </p>
          <span className="text-xs text-slate-500">
            {pensiones.total} cuota{pensiones.total !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatBox
            label="Pagadas"
            value={String(pensiones.pagadas)}
            sub={sol(pensiones.montoTotalPagado)}
          />
          <StatBox
            label="Pendientes"
            value={String(pensiones.pendientes)}
            sub={sol(pensiones.montoTotalTarifa - pensiones.montoTotalPagado)}
          />
          <StatBox
            label="Anuladas"
            value={String(pensiones.anuladas)}
          />
        </div>
        {/* Barra de progreso */}
        {pensiones.total > 0 && (
          <div className="px-4 pb-3">
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${(pensiones.pagadas / pensiones.total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-slate-400 mt-1.5 text-right">
              {Math.round((pensiones.pagadas / pensiones.total) * 100)}% pagado
            </p>
          </div>
        )}
      </div>
    )}
  </div>
);

// ── Sub-componente descuentos asignados ───────────────────────

const TIPO_STYLE: Record<string, string> = {
  PORCENTAJE: 'bg-blue-100 text-blue-700',
  MONTO_FIJO: 'bg-purple-100 text-purple-700',
};

const PanelDescuentos: React.FC<{
  tiposDescuento:  TipoDescuento[];
  descuentos:      DescuentoEstudiante[];
  addId:           string;
  onAddIdChange:   (v: string) => void;
  adding:          boolean;
  error:           string | null;
  onAsignar:       () => void;
  onEliminar:      (id: number) => void;
}> = ({ tiposDescuento, descuentos, addId, onAddIdChange, adding, error, onAsignar, onEliminar }) => {

  // IDs ya asignados para excluirlos del selector
  const asignadosIds = new Set(descuentos.map(d => d.tipoDescuentoId));
  const disponibles  = tiposDescuento.filter(t => !asignadosIds.has(t.idTipoDescuento));

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-5">
      <div className="px-6 py-4 bg-gradient-to-r from-teal-700 to-teal-500">
        <h2 className="text-white font-bold">Descuentos Asignados</h2>
        <p className="text-white/70 text-xs mt-0.5">
          Se aplicarán automáticamente en cada cobro
        </p>
      </div>

      <div className="p-6 space-y-4">

        {/* Lista de descuentos asignados */}
        {descuentos.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-3">
            Sin descuentos asignados
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {descuentos.map(d => (
              <div key={d.idDescuentoEstudiante}
                className="flex items-center gap-2 bg-slate-50 border border-slate-200
                           rounded-xl px-3 py-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                  ${TIPO_STYLE[d.tipoDescuentoTipo] ?? 'bg-slate-100 text-slate-600'}`}>
                  {d.tipoDescuentoTipo === 'PORCENTAJE' ? `${d.tipoDescuentoValor}%` : `S/ ${d.tipoDescuentoValor.toFixed(2)}`}
                </span>
                <span className="text-sm font-semibold text-slate-700">{d.tipoDescuentoNombre}</span>
                <button
                  onClick={() => onEliminar(d.idDescuentoEstudiante)}
                  className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                  title="Quitar descuento">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Agregar descuento */}
        {disponibles.length > 0 && (
          <div className="flex items-center gap-3 pt-1">
            <select
              value={addId}
              onChange={e => onAddIdChange(e.target.value)}
              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800
                         outline-none focus:ring-2 focus:ring-teal-500 transition-all bg-white">
              <option value="">— Seleccionar descuento —</option>
              {disponibles.map(t => (
                <option key={t.idTipoDescuento} value={t.idTipoDescuento}>
                  {t.nombre} ({t.tipo === 'PORCENTAJE' ? `${t.valor}%` : `S/ ${t.valor.toFixed(2)}`})
                </option>
              ))}
            </select>
            <button
              onClick={onAsignar}
              disabled={!addId || adding}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-700
                         text-white text-sm font-semibold transition-colors disabled:opacity-60 shadow-sm flex-shrink-0">
              {adding ? (
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"/>
                </svg>
              )}
              Agregar
            </button>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{error}</p>
        )}
      </div>
    </div>
  );
};

export default MatriculaFormPage;